/**
 * Product Brand Promotion slider runtime.
 *
 * Owns slide navigation, autoplay, and Theme Editor lifecycle for every active
 * section without duplicate listeners or intervals.
 */

(() => {
  const RUNTIME_KEY = '__MarineMaxProductBrandPromotion';
  const ROOT_SELECTOR = '[data-brand-promotion-slider]';
  const INSTANCE_KEY = '__productBrandPromotionInstance';
  const AUTOPLAY_DELAY = 5000;
  const existingRuntime = window[RUNTIME_KEY];

  if (existingRuntime?.initialize) {
    existingRuntime.initialize(document);
    return;
  }

  class ProductBrandPromotion {
    constructor(root) {
      if (!root) return;
      if (root[INSTANCE_KEY]) return root[INSTANCE_KEY];

      this.root = root;
      this.slidesContainer = root.querySelector('[data-slides]');
      this.dots = Array.from(root.querySelectorAll('[data-dot-index]'));
      this.slides = Array.from(root.querySelectorAll('[data-slide-index]'));
      this.currentSlide = 0;
      this.autoplayInterval = null;
      this.eventAbortController = new AbortController();
      root[INSTANCE_KEY] = this;

      this.initialize();
    }

    initialize() {
      if (!this.slidesContainer || this.slides.length <= 1) return;

      const listenerOptions = { signal: this.eventAbortController.signal };
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index), listenerOptions);
        dot.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;

            event.preventDefault();
            this.goToSlide(index);
          },
          listenerOptions,
        );
      });

      this.root.addEventListener('mouseenter', () => this.stopAutoplay(), listenerOptions);
      this.root.addEventListener('mouseleave', () => this.startAutoplay(), listenerOptions);
      this.startAutoplay();
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length || !this.slidesContainer) {
        return;
      }

      this.currentSlide = index;
      this.slidesContainer.style.transform = `translateX(-${index * 100}%)`;
      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle(
          'product-brand-promotion__dot--active',
          dotIndex === index,
        );
      });
    }

    startAutoplay() {
      if (this.slides.length <= 1) return;

      this.stopAutoplay();
      this.autoplayInterval = window.setInterval(() => {
        this.goToSlide((this.currentSlide + 1) % this.slides.length);
      }, AUTOPLAY_DELAY);
    }

    stopAutoplay() {
      if (this.autoplayInterval === null) return;

      window.clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }

    destroy() {
      if (this.isDestroyed) return;
      this.isDestroyed = true;

      this.stopAutoplay();
      this.eventAbortController.abort();
      if (this.root?.[INSTANCE_KEY] === this) {
        delete this.root[INSTANCE_KEY];
      }
    }
  }

  function collectRoots(root = document) {
    if (!root) return [];

    const roots = [];
    if (typeof root.matches === 'function' && root.matches(ROOT_SELECTOR)) {
      roots.push(root);
    }
    if (typeof root.querySelectorAll === 'function') {
      roots.push(...root.querySelectorAll(ROOT_SELECTOR));
    }

    return [...new Set(roots)];
  }

  function initialize(root = document) {
    return collectRoots(root).map(
      (slider) => slider[INSTANCE_KEY] || new ProductBrandPromotion(slider),
    );
  }

  function destroy(root) {
    collectRoots(root).forEach((slider) => {
      slider[INSTANCE_KEY]?.destroy?.();
    });
  }

  const runtime = Object.freeze({
    ProductBrandPromotion,
    destroy,
    initialize,
  });
  Object.defineProperty(window, RUNTIME_KEY, {
    configurable: true,
    enumerable: false,
    value: runtime,
    writable: false,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), {
      once: true,
    });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });
})();
